import { Box, Divider } from '@mui/material';
import { Button } from '@portals/core/src/components/Button/Button';
import { Dialog } from '@portals/core/src/components/Dialog/Dialog';
import { Typography } from '@portals/core/src/components/Typography/Typography';
import { ChevronLeft, ChevronLeftSmall } from '@portals/icons';
import { ImprintData } from '@portals/sip-client-data/src/general/ApiClientTypes';
import { getFixedT } from 'i18next';
import React, { useEffect } from 'react';

import { if6CssPrefix } from '../../../../config';
import { useIF6Context } from '../../../../context';
import { dialogPaperCSS, styles } from './EstateLegalModal.styles';
import { Imprint } from './Imprint/Imprint';

export type LegalDialogTypes = 'imprint' | 'privacyPolicy' | 'consumerInformation' | 'contact';

export interface EstateLegalModalProps {
  open: boolean;
  onClose: () => void;
  dialogType: LegalDialogTypes;
  content?: string | ImprintData | React.ReactNode;
}

const t = getFixedT(null, 'core-immobilien');

export const EstateLegalModal: React.FC<EstateLegalModalProps> = ({ open, onClose, dialogType, content }) => {
  const isIF6Scope = useIF6Context();

  const getParentContainer = () => {
    if (isIF6Scope && typeof window !== 'undefined') {
      return document.getElementsByClassName(if6CssPrefix)[0];
    }
    return undefined;
  };

  useEffect(() => {
    if (isIF6Scope) {
      const overflow = open ? 'hidden' : '';
      document.documentElement.style.overflow = overflow;
      document.body.style.overflow = overflow;
      return () => {
        document.documentElement.style.overflow = '';
        document.body.style.overflow = '';
      };
    }
  }, [open, isIF6Scope]);

  return (
    <Dialog
      container={getParentContainer()}
      open={open}
      onClose={onClose}
      className="legal-modal-container"
      fullScreen
      scroll="paper"
      PaperProps={{
        className: 'legal-modal-container-paper',
        style: dialogPaperCSS,
      }}
      sx={styles.dialogContainer}
    >
      <Box sx={styles.dialogPaperWrapper}>
        <Box sx={styles.dialogHeader} className="legal-modal-header">
          <Button onClick={onClose} variant="inverted" sx={styles.closeButton}>
            <ChevronLeftSmall sx={styles.closeButtonSvgSM} />
            <ChevronLeft sx={styles.closeButtonSvgLG} />
            {t('back')}
          </Button>
          <Divider />
        </Box>
        <Box sx={styles.dialogContent} className="legal-modal-content">
          <Typography variant="h3" component="h3" sx={styles.title} className="legal-modal-title">
            {dialogType === 'contact' && t('detailPage.contractProvider')}
            {dialogType === 'imprint' && t('detailPage.imprintHeadline')}
            {dialogType === 'privacyPolicy' && t('detailPage.privacyPolicyHeadline')}
            {dialogType === 'consumerInformation' && t('detailPage.consumerInformationHeadline')}
          </Typography>

          {dialogType === 'contact' && React.isValidElement(content) && content}
          {dialogType === 'imprint' && <Imprint data={content as ImprintData} />}
          {(dialogType === 'privacyPolicy' || dialogType === 'consumerInformation') && (
            <Typography variant="body2" component="p" dangerouslySetInnerHTML={{ __html: content?.toString() || '' }} />
          )}
        </Box>
      </Box>
    </Dialog>
  );
};

EstateLegalModal.displayName = 'EstateLegalModal';
